import campaignApi from '@/services/calls/campaign'
import {
  Campaign,
  CampaignAction,
  CampaignLink,
  CampaignLinksResponse,
  CampaignModalitiesOverridesResponse,
  CampaignProductOverridesResponse,
  CampaignsResponse,
  CreateCampaignLinkPayload,
  SelectedProductForCampaign,
} from '@/types/campaign'
import { Region } from '@/types/misc'
import { StorageSerializers, useStorage } from '@vueuse/core'
import { differenceInDays, format, sub } from 'date-fns'
import { toZonedTime } from 'date-fns-tz'
import { defineStore } from 'pinia'

interface States {
  campaigns: Ref<CampaignsResponse | null>
  campaignData: Ref<(Campaign & { products: SelectedProductForCampaign[] }) | null>
  campaignLinks: Ref<CampaignLinksResponse | null>
  analyticsFilters: Ref<{
    selectedDateRange: {
      startDate: Date
      endDate: Date
    }
    campaignLink: CampaignLink | null
    region: Region | null
  }>
  productRules: Ref<{ productId: number; rules: { name: string; values: { value: string; text: string }[] }[] }[]>
  campaignProductOverrides: Ref<CampaignProductOverridesResponse | null>
  campaignModalityOverrides: Ref<CampaignModalitiesOverridesResponse | null>
}

export const useCampaignStore = defineStore('campaign', () => {
  const states: States = {
    campaigns: ref(null),
    campaignData: ref(null),
    campaignLinks: ref(null),
    analyticsFilters: useStorage(
      'campaignAnalyticsDateRange',
      {
        selectedDateRange: {
          startDate: sub(toZonedTime(new Date(), 'UTC'), { days: 7 }),
          endDate: toZonedTime(new Date(), 'UTC'),
        },
        campaignLink: null,
        region: null,
      },
      undefined,
      { serializer: StorageSerializers.object },
    ),
    productRules: ref([]),
    campaignProductOverrides: ref(null),
    campaignModalityOverrides: ref(null),
  }

  const getAnalyticsSelectedDatesDifference = computed(() => {
    return differenceInDays(
      states.analyticsFilters.value.selectedDateRange.startDate,
      states.analyticsFilters.value.selectedDateRange.endDate,
    )
  })

  const getSelectedDateRangeForCSVName = computed(() => {
    const startDate = format(states.analyticsFilters.value.selectedDateRange.startDate, 'yyyy-MM-dd')
    const endDate = format(states.analyticsFilters.value.selectedDateRange.endDate, 'yyyy-MM-dd')
    return `${startDate}-${endDate}`
  })

  const resetAnalyticsFilters = () => {
    states.analyticsFilters.value.campaignLink = null
    states.analyticsFilters.value.region = null
  }

  const createCampaignLink = async (campaignId: number, payload: CreateCampaignLinkPayload) => {
    const response = await campaignApi.createLink(campaignId, payload)

    if (states.campaignLinks.value) {
      states.campaignLinks.value.results.push(response)
    }
  }

  const updateCampaignLink = async (
    campaignId: number,
    campaignLinkId: number,
    payload: Partial<CreateCampaignLinkPayload>,
  ) => {
    const response = await campaignApi.updateLink(campaignId, campaignLinkId, payload)

    if (states.campaignLinks.value) {
      const linkIndex = states.campaignLinks.value.results.findIndex((link) => link.id === campaignLinkId)
      if (linkIndex !== -1) {
        states.campaignLinks.value.results[linkIndex] = response
      }
    }
  }

  const deleteCampaignLink = async (campaignId: number, linkId: number) => {
    await campaignApi.deleteLink(campaignId, linkId)

    if (!states.campaignLinks.value) return
    const linkIndexToDelete = states.campaignLinks.value.results.findIndex((link) => link.id === linkId)
    if (linkIndexToDelete === -1) return
    states.campaignLinks.value.results.splice(linkIndexToDelete, 1)
  }

  const changeCampaignStatus = async (campaignId: number, action: CampaignAction) => {
    await campaignApi.changeCampaignStatus(campaignId, action)

    if (states.campaigns.value) {
      const campaignIndex = states.campaigns.value.results.findIndex((campaign) => campaign.id === campaignId)
      if (campaignIndex !== -1) {
        states.campaigns.value.results.splice(campaignIndex, 1)
        states.campaigns.value.total_data--
      }
    }
  }

  const resetStore = () => {
    states.campaigns.value = null
    resetAnalyticsFilters()
    states.campaignLinks.value = null
  }

  return {
    ...states,
    createCampaignLink,
    deleteCampaignLink,
    changeCampaignStatus,
    updateCampaignLink,
    getAnalyticsSelectedDatesDifference,
    resetAnalyticsFilters,
    resetStore,
    getSelectedDateRangeForCSVName,
  }
})
